import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';

const NAV_ITEMS = [
  { label: 'Главная', href: '/' },
  { label: 'О компании', href: '/about' },
  { label: 'Контакты', href: '/contacts' },
];

const PRODUCT_ITEMS = [
  { label: 'Навесы для автомобилей (машин)', href: '/products/carports' },
  { label: 'Козырьки', href: '/products/canopies' },
  { label: 'Беседки', href: '/products/gazebos' },
  { label: 'Ворота и калитки', href: '/products/gates' },
  { label: 'Мангальные зоны', href: '/products/bbq' },
  { label: 'Кованые оградки', href: '/products/grave-fences' },
  { label: 'Перила', href: '/products/railings' },
  { label: 'Каминные наборы', href: '/products/fireplace-sets' },
  { label: 'Кованная мебель', href: '/products/wrought-furniture' },
  { label: 'Решётки для окон', href: '/products/window-grilles' },
  { label: 'Качели', href: '/products/swings' },
  { label: 'Скамейки', href: '/products/benches' },
];

const WA_TEXT = encodeURIComponent('Здравствуйте! Хочу рассчитать стоимость изделия. Подскажите, пожалуйста, варианты и цены.');
export default function Navbar() {
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [ctaOpen, setCtaOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const dropdownTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const ctaRef = useRef<HTMLDivElement>(null);

  const hasHero = location.pathname === '/' || location.pathname.startsWith('/products/');
  const isTransparent = hasHero && !scrolled;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setDropdownOpen(false);
    setCtaOpen(false);
  }, [location.pathname]);

  // Close CTA popup when clicking outside
  useEffect(() => {
    if (!ctaOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (ctaRef.current && !ctaRef.current.contains(e.target as Node)) {
        setCtaOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [ctaOpen]);

  // Prevent body scroll when mobile menu open
  useEffect(() => {
    document.body.style.overflow = mobileOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  const isActive = (href: string) => location.pathname === href;
  const isProductActive = PRODUCT_ITEMS.some((p) => location.pathname === p.href);

  const handleMouseEnter = () => {
    if (dropdownTimeout.current) {
      clearTimeout(dropdownTimeout.current);
      dropdownTimeout.current = null;
    }
    setDropdownOpen(true);
  };

  const handleMouseLeave = () => {
    dropdownTimeout.current = setTimeout(() => setDropdownOpen(false), 200);
  };

  const headerBg = isTransparent
    ? 'bg-transparent'
    : 'bg-background-50/95 backdrop-blur-md shadow-[0_1px_3px_rgba(0,0,0,0.08)]';

  const logoColor = isTransparent ? 'text-background-50' : 'text-foreground-950';
  const logoSubColor = isTransparent ? 'text-background-50/70' : 'text-foreground-600';

  const linkColor = (active: boolean) => {
    if (isTransparent) return active ? 'text-primary-400' : 'text-background-50/80 hover:text-background-50';
    return active ? 'text-primary-500' : 'text-foreground-700 hover:text-primary-500';
  };

  const phoneColor = isTransparent ? 'text-background-50 hover:text-primary-300' : 'text-foreground-900 hover:text-primary-500';
  const hamburgerColor = isTransparent ? 'text-background-50' : 'text-foreground-950';

  return (
    <>
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${headerBg}`}>
        <nav className="mx-auto flex items-center justify-between px-4 md:px-6 py-3 lg:py-4" style={{ maxWidth: '1400px' }}>
          {/* Logo */}
          <Link to="/" className="flex items-center shrink-0">
            <img
              src="https://static.readdy.ai/image/401a812800a3c57fa50042dc76ab6cbb/0256ded7d4a57ab7e0ad2400f24bc9fc.png"
              alt="Художественная Ковка"
              className="h-7 md:h-8 w-auto transition-all duration-300"
              style={{ filter: isTransparent ? 'brightness(0) invert(1)' : 'none' }}
            />
          </Link>

          {/* Desktop nav */}
          <div className="hidden md:flex items-center gap-8">
            {NAV_ITEMS.filter((item) => item.href === '/').map((item) => (
              <Link
                key={item.href}
                to={item.href}
                className={`relative text-sm font-medium tracking-wide whitespace-nowrap transition-colors duration-300 py-1 ${linkColor(isActive(item.href))}`}
              >
                {item.label}
                {isActive(item.href) && (
                  <span className="absolute -bottom-0.5 left-0 right-0 h-[2px] bg-primary-500 rounded-full" />
                )}
              </Link>
            ))}

            {/* Изделия dropdown */}
            <div
              ref={dropdownRef}
              className="relative"
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
            >
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className={`relative flex items-center gap-1.5 text-sm font-medium tracking-wide whitespace-nowrap transition-colors duration-300 py-1 ${linkColor(isProductActive)}`}
              >
                Изделия
                <span className="w-4 h-4 flex items-center justify-center transition-transform duration-300" style={{ transform: dropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}>
                  <i className="ri-arrow-down-s-line text-sm"></i>
                </span>
                {isProductActive && (
                  <span className="absolute -bottom-0.5 left-0 right-0 h-[2px] bg-primary-500 rounded-full" />
                )}
              </button>

              <div
                className={`absolute top-full left-1/2 -translate-x-1/2 mt-2 w-64 bg-background-50 border border-background-200/70 rounded-lg shadow-[0_8px_30px_rgba(0,0,0,0.12)] overflow-hidden transition-all duration-300 origin-top ${
                  dropdownOpen ? 'opacity-100 scale-100 pointer-events-auto' : 'opacity-0 scale-95 pointer-events-none'
                }`}
              >
                <div className="py-2">
                  {PRODUCT_ITEMS.map((product) => (
                    <Link
                      key={product.href}
                      to={product.href}
                      className={`block px-5 py-2.5 text-sm transition-colors duration-200 ${
                        location.pathname === product.href
                          ? 'text-primary-500 bg-primary-50 font-medium'
                          : 'text-foreground-700 hover:text-primary-500 hover:bg-background-100'
                      }`}
                    >
                      {product.label}
                    </Link>
                  ))}
                </div>
              </div>
            </div>

            {NAV_ITEMS.filter((item) => item.href !== '/').map((item) => (
              <Link
                key={item.href}
                to={item.href}
                className={`relative text-sm font-medium tracking-wide whitespace-nowrap transition-colors duration-300 py-1 ${linkColor(isActive(item.href))}`}
              >
                {item.label}
                {isActive(item.href) && (
                  <span className="absolute -bottom-0.5 left-0 right-0 h-[2px] bg-primary-500 rounded-full" />
                )}
              </Link>
            ))}
          </div>

          {/* Right side */}
          <div className="hidden md:flex items-center gap-5">
            <a
              href="tel:+79174131562"
              className={`flex items-center gap-2 text-sm font-semibold tracking-wide whitespace-nowrap transition-colors duration-300 ${phoneColor}`}
            >
              <span className="w-5 h-5 flex items-center justify-center">
                <i className="ri-phone-fill text-base"></i>
              </span>
              +7 917 413-15-62
            </a>

            {/* CTA button with popup */}
            <div ref={ctaRef} className="relative">
              <button
                onClick={() => setCtaOpen(!ctaOpen)}
                className="whitespace-nowrap bg-primary-500 hover:bg-primary-600 text-background-50 font-semibold text-sm px-6 py-2.5 rounded-md transition-all duration-300 hover:scale-[1.03] active:scale-[0.97] cursor-pointer flex items-center gap-2"
              >
                Рассчитать стоимость
                <span className="w-4 h-4 flex items-center justify-center transition-transform duration-300" style={{ transform: ctaOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}>
                  <i className="ri-arrow-down-s-line text-sm"></i>
                </span>
              </button>

              {/* CTA Popup */}
              <div
                className={`absolute top-full right-0 mt-3 w-72 bg-background-50 border border-background-200/70 rounded-xl shadow-[0_12px_40px_rgba(0,0,0,0.15)] overflow-hidden transition-all duration-300 origin-top-right ${
                  ctaOpen ? 'opacity-100 scale-100 pointer-events-auto' : 'opacity-0 scale-95 pointer-events-none'
                }`}
              >
                <div className="px-5 pt-5 pb-2">
                  <p className="text-sm font-semibold text-foreground-950 mb-1">Выберите способ связи</p>
                  <p className="text-xs text-foreground-500">Напишите нам — рассчитаем стоимость в&nbsp;течение дня</p>
                </div>
                <div className="px-4 pb-4 flex flex-col gap-2 mt-2">
                  <a
                    href={`https://wa.me/79174131562?text=${WA_TEXT}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => setCtaOpen(false)}
                    className="flex items-center gap-3 px-4 py-3 rounded-lg bg-[#25D366]/10 hover:bg-[#25D366]/20 text-[#1a9e4a] font-medium text-sm transition-all duration-200 cursor-pointer"
                  >
                    <span className="w-8 h-8 flex items-center justify-center rounded-full bg-[#25D366] text-background-50">
                      <i className="ri-whatsapp-line text-base"></i>
                    </span>
                    WhatsApp
                  </a>

                  <a
                    href="https://max.ru/u/f9LHodD0cOICoHyOC2LT2Vx_42oC8Zdm2VV9Uv5O-6mGvkZgeZzUcJQkER0"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => setCtaOpen(false)}
                    className="flex items-center gap-3 px-4 py-3 rounded-lg bg-accent-50 hover:bg-accent-100 text-accent-700 font-medium text-sm transition-all duration-200 cursor-pointer"
                  >
                    <span className="w-8 h-8 flex items-center justify-center rounded-full bg-accent-600 text-background-50">
                      <i className="ri-chat-3-line text-base"></i>
                    </span>
                    MAX
                  </a>

                  <a
                    href="https://t.me/Gmgmg0202"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => setCtaOpen(false)}
                    className="flex items-center gap-3 px-4 py-3 rounded-lg bg-[#2AABEE]/10 hover:bg-[#2AABEE]/20 text-[#1a87c2] font-medium text-sm transition-all duration-200 cursor-pointer"
                  >
                    <span className="w-8 h-8 flex items-center justify-center rounded-full bg-[#2AABEE] text-background-50">
                      <i className="ri-telegram-line text-base"></i>
                    </span>
                    Telegram
                  </a>

                  <a
                    href="tel:+79174131562"
                    onClick={() => setCtaOpen(false)}
                    className="flex items-center gap-3 px-4 py-3 rounded-lg bg-primary-50 hover:bg-primary-100 text-primary-700 font-medium text-sm transition-all duration-200 cursor-pointer"
                  >
                    <span className="w-8 h-8 flex items-center justify-center rounded-full bg-primary-500 text-background-50">
                      <i className="ri-phone-fill text-base"></i>
                    </span>
                    Позвонить
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Mobile hamburger */}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className={`md:hidden w-10 h-10 flex items-center justify-center rounded-md transition-colors ${hamburgerColor}`}
            aria-label="Меню"
          >
            <i className={`text-xl ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </nav>

        {/* Mobile menu */}
        <div
          className={`md:hidden overflow-hidden transition-all duration-400 ${
            mobileOpen ? 'max-h-[700px] opacity-100' : 'max-h-0 opacity-0'
          } bg-background-50/98 backdrop-blur-md border-t border-background-200/50`}
        >
          <div className="px-6 py-4 flex flex-col gap-1">
            {NAV_ITEMS.filter((item) => item.href === '/').map((item) => (
              <Link
                key={item.href}
                to={item.href}
                className={`text-base font-medium tracking-wide py-2.5 transition-colors ${
                  isActive(item.href) ? 'text-primary-500' : 'text-foreground-800 hover:text-primary-500'
                }`}
              >
                {item.label}
              </Link>
            ))}

            <div>
              <button
                onClick={(e) => { e.stopPropagation(); setDropdownOpen(!dropdownOpen); }}
                className={`w-full flex items-center justify-between text-base font-medium tracking-wide py-2.5 transition-colors ${
                  isProductActive ? 'text-primary-500' : 'text-foreground-800 hover:text-primary-500'
                }`}
              >
                Изделия
                <span className="w-5 h-5 flex items-center justify-center transition-transform duration-300" style={{ transform: dropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}>
                  <i className="ri-arrow-down-s-line"></i>
                </span>
              </button>
              <div className={`overflow-hidden transition-all duration-300 ${dropdownOpen ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}>
                <div className="pl-4 py-1 flex flex-col gap-0.5 border-l-2 border-background-200 ml-1">
                  {PRODUCT_ITEMS.map((product) => (
                    <Link
                      key={product.href}
                      to={product.href}
                      className={`text-sm py-2 transition-colors ${
                        location.pathname === product.href ? 'text-primary-500 font-medium' : 'text-foreground-600 hover:text-primary-500'
                      }`}
                    >
                      {product.label}
                    </Link>
                  ))}
                </div>
              </div>
            </div>

            {NAV_ITEMS.filter((item) => item.href !== '/').map((item) => (
              <Link
                key={item.href}
                to={item.href}
                className={`text-base font-medium tracking-wide py-2.5 transition-colors ${
                  isActive(item.href) ? 'text-primary-500' : 'text-foreground-800 hover:text-primary-500'
                }`}
              >
                {item.label}
              </Link>
            ))}

            <div className="h-px bg-background-200 my-2" />

            {/* Mobile CTA buttons */}
            <p className="text-xs text-foreground-500 mb-1">Рассчитать стоимость:</p>
            <div className="flex flex-col gap-2">
              <a
                href={`https://wa.me/79174131562?text=${WA_TEXT}`}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setMobileOpen(false)}
                className="flex items-center gap-3 px-4 py-3 rounded-lg bg-[#25D366] text-background-50 font-medium text-sm transition-all duration-200 cursor-pointer"
              >
                <span className="w-5 h-5 flex items-center justify-center"><i className="ri-whatsapp-line text-lg"></i></span>
                WhatsApp
              </a>
              <a
                href="https://max.ru/u/f9LHodD0cOICoHyOC2LT2Vx_42oC8Zdm2VV9Uv5O-6mGvkZgeZzUcJQkER0"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setMobileOpen(false)}
                className="flex items-center gap-3 px-4 py-3 rounded-lg bg-accent-600 text-background-50 font-medium text-sm transition-all duration-200 cursor-pointer"
              >
                <span className="w-5 h-5 flex items-center justify-center"><i className="ri-chat-3-line text-lg"></i></span>
                MAX
              </a>
              <a
                href="https://t.me/Gmgmg0202"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setMobileOpen(false)}
                className="flex items-center gap-3 px-4 py-3 rounded-lg bg-[#2AABEE] text-background-50 font-medium text-sm transition-all duration-200 cursor-pointer"
              >
                <span className="w-5 h-5 flex items-center justify-center"><i className="ri-telegram-line text-lg"></i></span>
                Telegram
              </a>
              <a
                href="tel:+79174131562"
                onClick={() => setMobileOpen(false)}
                className="flex items-center gap-3 px-4 py-3 rounded-lg bg-primary-500 text-background-50 font-medium text-sm transition-all duration-200 cursor-pointer"
              >
                <span className="w-5 h-5 flex items-center justify-center"><i className="ri-phone-fill text-lg"></i></span>
                Позвонить
              </a>
            </div>
          </div>
        </div>
      </header>

      {/* CTA popup overlay (mobile close on overlay click) */}
      {ctaOpen && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setCtaOpen(false)}
        />
      )}
    </>
  );
}